import { Link } from 'react-router-dom';
import { LegalLayout } from '../components/LegalLayout';
import '../legal.css';

export function DeprecationPage() {
  return (
    <LegalLayout
      title="Deprecation policy"
      updated="June 1, 2026"
      summary="12-month minimum notice on breaking API changes, with a clear migration path. How removals are communicated and how to subscribe to upcoming-deprecation announcements."
      needsReview={true}
    >
      <h2>The commitment in one paragraph</h2>
      <p>
        Rynora will not break a stable API surface, contract type, webhook payload, CLI flag, or admin-config schema
        without at least <strong>12 months</strong> notice and a clearly documented migration path. The notice and the
        migration path are published on this page and emailed to every org admin at least 30 days before the deprecation
        becomes effective.
      </p>

      <h2>What counts as a "stable" surface</h2>
      <ul>
        <li>
          Anything documented under <Link to="/docs/api">/docs/api</Link> without an explicit <code>beta</code> or{' '}
          <code>preview</code> tag.
        </li>
        <li>
          The contract types in <code>@rynora/contracts</code> (every published Zod schema).
        </li>
        <li>The webhook payloads documented in the API reference.</li>
        <li>
          The <code>rynora</code> CLI flags documented in <code>rynora --help</code>.
        </li>
        <li>
          The admin-config schema in <code>@rynora/api-config</code>.
        </li>
        <li>The publicly-documented database tables and views (for self-hosted operators reading the DB directly).</li>
      </ul>

      <h2>What is explicitly NOT stable</h2>
      <ul>
        <li>
          Anything tagged <code>beta</code> or <code>preview</code> in product copy or docs. Beta APIs can change with
          30-day notice; preview APIs can change with 7-day notice.
        </li>
        <li>
          Internal implementation details — function signatures inside the source, internal DB columns prefixed{' '}
          <code>_</code>, log message formats, performance characteristics.
        </li>
        <li>
          The HTML / DOM / CSS structure of any Rynora-rendered page. Tests written against Rynora's own UI are not
          something we commit to support.
        </li>
        <li>
          The default model / provider behind each AI feature. We do reserve the right to change defaults as model
          quality changes; the <Link to="/trust/ai">AIBOM</Link> documents the current defaults and is updated on
          change.
        </li>
        <li>The visual appearance of the UI (themes, layouts, copy). UI evolution is not a deprecation event.</li>
      </ul>

      <h2>How a deprecation moves through its lifecycle</h2>
      <ol>
        <li>
          <strong>T-12 months: Announced.</strong> A new row appears on this page with the deprecated surface, the
          target removal date, the migration path, and the rationale. The same announcement goes to a public RSS feed
          and is emailed to every org admin.
        </li>
        <li>
          <strong>T-12 to T-3 months: Warned in code.</strong> The deprecated surface emits a <code>Deprecation</code>
          HTTP header (RFC 8594) and a runtime warning in the CLI / SDK. Customer-side logs and traces surface the
          warning so engineers see it during normal development.
        </li>
        <li>
          <strong>T-3 months: Email reminder.</strong> A second email goes to every org admin whose org has used the
          deprecated surface in the last 30 days, with the org-specific usage count and a link to the migration guide.
        </li>
        <li>
          <strong>T-30 days: Final notice.</strong> A third email goes to every org admin whose org is still using the
          deprecated surface, with a "this will break on &lt;date&gt;" headline and a link to the migration guide.
        </li>
        <li>
          <strong>T-0: Removed.</strong> The surface returns the announced replacement behaviour (often a 410 Gone on
          the API side, or a no-op + error log on the CLI side). The replacement surface has been available since the
          original announcement at T-12.
        </li>
      </ol>

      <h2>Migration support</h2>
      <p>Every deprecation announcement is paired with a written migration guide that includes:</p>
      <ul>
        <li>The exact deprecated surface and its replacement, side-by-side.</li>
        <li>A code-level diff for the most common usage pattern (curl, Node, Python SDK).</li>
        <li>
          A copy-pasteable <code>codemod</code> or one-shot script where mechanical migration is possible (for example,
          contract-type renames).
        </li>
        <li>An estimate of the time-to-migrate for the median customer.</li>
        <li>Contact details for the on-call engineer who owns the migration question backlog for that deprecation.</li>
      </ul>

      <h2>How to subscribe</h2>
      <ul>
        <li>
          Email subscription: send <code>SUBSCRIBE</code> to{' '}
          <a href="mailto:deprecations@rynora.app?subject=SUBSCRIBE">deprecations@rynora.app</a>. We send one email per
          deprecation event; unsubscribe by replying <code>UNSUBSCRIBE</code>.
        </li>
        <li>
          RSS feed: <code>https://rynora.app/deprecations.rss</code> (planned, see the{' '}
          <Link to="/handbook/quality-enterprise-compliance-checklist">enterprise compliance checklist</Link>).
        </li>
        <li>
          In-product: org admins see a banner in Admin Settings when any deprecated surface is used by their org in the
          last 30 days.
        </li>
      </ul>

      <h2>Currently announced deprecations</h2>
      <p>
        <em>None.</em> The first row will appear on this page when the first formal deprecation is announced. Until
        then, every documented stable surface is committed to its current shape.
      </p>

      <h2>Past deprecations (audit trail)</h2>
      <p>
        <em>None — this is the v1 deprecation policy.</em> Every deprecation removed after the v1 policy took effect
        will be archived in this section with the original announcement date, the removal date, and the migration guide
        URL.
      </p>

      <h2>Exceptional cases</h2>
      <ul>
        <li>
          <strong>Security-driven removal.</strong> A surface that is the subject of a security disclosure may be
          removed faster than 12 months. We will publish the security advisory simultaneously and provide the longest
          notice the disclosure window allows.
        </li>
        <li>
          <strong>Provider-driven removal.</strong> If a sub-processor (LLM provider, hosting platform) discontinues a
          dependency, we will give as much notice as we have and provide a replacement before removal. The 12-month
          commitment does not bind us to maintain a feature for which the underlying dependency no longer exists.
        </li>
        <li>
          <strong>Legal / regulatory removal.</strong> A surface that must be removed to comply with new law or
          regulation may be removed on the legally-required timeline. We will document the law / regulation in the
          deprecation row.
        </li>
      </ul>

      <h2>Contractual primacy</h2>
      <p>
        This page is the deprecation policy; the <strong>Master Services Agreement</strong> between Rynora and the
        customer is the binding source of truth for the deprecation commitment. Where this page and the MSA conflict,
        the MSA controls.
      </p>
    </LegalLayout>
  );
}
